import { createApp, h } from 'vue';
import { createInertiaApp, Link, Head } from '@inertiajs/vue3';  // Update Inertia import
import { createPinia } from 'pinia';
import * as Sentry from "@sentry/vue";

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/pro-solid-svg-icons';
import { far} from '@fortawesome/pro-regular-svg-icons';
import { ZiggyVue } from 'ziggy-js';
import Toasted from "@hoppscotch/vue-toasted"
import "@hoppscotch/vue-toasted/style.css"

import _ from 'lodash';
window._ = _;

import Confirm from './composables/useConfirm';
window.Confirm = Confirm;

import moment from 'moment';
window.moment = moment;

import axios from 'axios';
window.axios = axios;

// Add default headers
window.axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

// Add interceptor to handle 401 responses
window.axios.interceptors.response.use(
    response => response,
    error => {
        // Redirect to login page if we get a 401 (Unauthorized)
        if (error.response && error.response.status === 401) {
            window.location.href = '/login';
        }
        return Promise.reject(error);
    }
);

library.add(far, fas);

import Multiselect from 'vue-multiselect'

// Jet Components
import JetDialogModal from "@/Jetstream/DialogModal";
import JetDangerButton from "@/Jetstream/DangerButton";
import JetButton from "@/Jetstream/Button";
import JetInput from "@/Jetstream/Input";
import JetFormSection from "@/Jetstream/FormSection";
import JetActionMessage from "@/Jetstream/ActionMessage";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import JetDropDown from '@/Jetstream/Dropdown.vue';
import JetApplicationMark from '@/Jetstream/ApplicationMark.vue';

//MazUI Components
import MazInput from 'maz-ui/components/MazInput'
import MazPhoneNumberInput from 'maz-ui/components/MazPhoneNumberInput'
import MazPicker from 'maz-ui/components/MazPicker'
import MazRadio from 'maz-ui/components/MazRadio'
import MazDropzone from 'maz-ui/components/MazDropzone'


// App Components
import GeneralTable from "@/components/GeneralTable/GeneralTable";
import GroupSelect from "@/components/GroupSelect";
import ContactSelect from "@/components/ContactSelect";
import Tooltip from "@/components/Tooltip";
import CustomFieldSelector from '@/components/CustomFieldSelector.vue';
import StateSelector from '@/components/Address/StateSelector.vue';
import ProvinceSelector from '@/components/Address/ProvinceSelector.vue';
import AppLayout from "@/Layouts/AppLayout";
import DateTooltip from "@/components/DateTooltip";

//style imports
import 'maz-ui/styles';
import '../css/app.css'
import 'vue-multiselect/dist/vue-multiselect.min.css';

createInertiaApp({
    resolve: name => {
        const pages = import.meta.glob('./Pages/**/*.vue', { eager: true })
        return pages[`./Pages/${name}.vue`]
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })

        Sentry.init({
            app,
            dsn: "https://8d8712c3297e43c89b1777730da97b0d@o938554.ingest.sentry.io/5888336",
            integrations: [
                Sentry.browserTracingIntegration(),
            ],
            // Configure sample rate for performance monitoring
            tracesSampleRate: 0.1,
            // Link frontend errors with backend errors
            tracePropagationTargets: [/^https:\/\/textspot/, /^\//],
            // Track releases and environment
            environment: import.meta.env.MODE || "development",
        });

        app.use(plugin)
        app.use(createPinia())
        app.use(ZiggyVue);
        app.use(Toasted)

        // Global Components
        app.component('Link', Link)
        app.component('Head', Head)
        app.component('font-awesome-icon', FontAwesomeIcon)

        // Jetstream components
        app.component('jet-dialog-modal', JetDialogModal)
        app.component('jet-danger-button', JetDangerButton)
        app.component('jet-button', JetButton)
        app.component('jet-input', JetInput)
        app.component('jet-form-section', JetFormSection)
        app.component('jet-action-message', JetActionMessage)
        app.component('jet-input-error', JetInputError)
        app.component('jet-label', JetLabel)
        app.component('jet-primary-button', PrimaryButton)
        app.component('jet-secondary-button', SecondaryButton)
        app.component('jet-checkbox', JetCheckbox)
        app.component('jet-dropdown', JetDropDown)
        app.component('jet-application-mark', JetApplicationMark)

        // MazUI components
        app.component('maz-input', MazInput)
        app.component('maz-phone-number-input', MazPhoneNumberInput)
        app.component('maz-picker', MazPicker)
        app.component('maz-radio', MazRadio)
        app.component('maz-dropzone', MazDropzone)


        // App components
        app.component('general-table', GeneralTable)
        app.component('contact-select', ContactSelect)
        app.component('app-layout', AppLayout)
        app.component('date-tooltip', DateTooltip)
        app.component('group-select', GroupSelect)
        app.component('tooltip', Tooltip)
        app.component('custom-field-selector', CustomFieldSelector)
        app.component('multiselect', Multiselect)
        app.component('state-selector', StateSelector)
        app.component('province-selector', ProvinceSelector)
        
        app.mount(el)
    },
    title: title => `${title} - TextSpot`,
})